// ============================================
// APP PRINCIPAL - BILEADS FRONTEND (FASE 26)
// ============================================
// INCLUYE: Sistema de Planes Multi-tenant + Super Admin Panel + Historial Clientes + Catálogo
// ✅ F25B: Ruta MetaTemplatesPage
// ✅ F26: Rutas de Operaciones (Stock, OTs, Remitos, Facturación, Relevamientos)

import { useEffect, lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { ToastProvider } from './context/ToastContext';
import { SocketProvider } from './context/SocketContext';
import toast, { Toaster } from 'react-hot-toast';
import ProtectedRoute from './components/ProtectedRoute';
import Layout from './components/Layout';
import DemoTour from './components/DemoTour';

// Páginas principales (carga lazy)
const LoginPage = lazy(() => import('./pages/LoginPage'));
const RegisterPage = lazy(() => import('./pages/RegisterPage'));
const DashboardPage = lazy(() => import('./pages/DashboardPage'));
const LeadsPage = lazy(() => import('./pages/LeadsPage'));
const ChatPage = lazy(() => import('./pages/ChatPage'));
const CampaignsPage = lazy(() => import('./pages/CampaignsPage'));
const BroadcastsPage = lazy(() => import('./pages/BroadcastsPage'));
const BotsPage = lazy(() => import('./pages/BotsPage'));
const PlansPage = lazy(() => import('./pages/PlansPage'));

// Super Admin
const AdminDashboardPage = lazy(() => import('./pages/AdminDashboardPage'));
const AdminClientsPage = lazy(() => import('./pages/AdminClientsPage'));
const AdminBotTemplatesPage = lazy(() => import('./pages/AdminBotTemplatesPage'));
const AdminConfigPage = lazy(() => import('./pages/AdminConfigPage'));

// Ventas + Historial
const PipelinePage = lazy(() => import('./pages/PipelinePage'));
const SalesPage = lazy(() => import('./pages/SalesPage'));
const PaymentsPage = lazy(() => import('./pages/PaymentsPage'));
const ClienteHistorialPage = lazy(() => import('./pages/ClienteHistorialPage'));
const LeadTimelinePage = lazy(() => import('./pages/LeadTimelinePage'));
const CuentaCorrientePage = lazy(() => import('./pages/CuentaCorrientePage'));

// Marketing + Catálogo
const ProductCatalogPage = lazy(() => import('./pages/ProductCatalogPage'));
const MetaTemplatesPage = lazy(() => import('./pages/MetaTemplatesPage'));
const InformesPage = lazy(() => import('./pages/InformesPage'));

// Operaciones
const StockPage = lazy(() => import('./pages/StockPage'));
const OrdenesTrabajoPage = lazy(() => import('./pages/OrdenesTrabajoPage'));
const RemitosPage = lazy(() => import('./pages/RemitosPage'));
const FacturacionPage = lazy(() => import('./pages/FacturacionPage'));
const RelevamientoPage = lazy(() => import('./pages/RelevamientoPage'));

// Configuración
const PlantillaPresupuestoPage = lazy(() => import('./pages/PlantillaPresupuestoPage'));

// ✅ S53-B2: Auditoría leads eliminados
const LeadsEliminadosPage = lazy(() => import('./pages/LeadsEliminadosPage'));
// ✅ S64: Análisis de Leads (Funnel)
const AnalisisLeadsPage = lazy(() => import('./pages/AnalisisLeadsPage'));
const ServiceAlertsPage = lazy(() => import('./pages/ServiceAlertsPage'));
const ConfigPagosPage = lazy(() => import('./pages/ConfigPagosPage'));
const RecordatoriosPage = lazy(() => import('./pages/RecordatoriosPage'));
const DashboardEjecutivoPage = lazy(() => import('./pages/DashboardEjecutivoPage'));

// Componentes globales (NO lazy — se usan siempre)
import LeadAlertOverlay from './components/LeadAlertOverlay';

import { useAuth } from './context/AuthContext';

// Spinner de carga para Suspense
const PageLoader = () => (
  <div style={{
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '60vh',
    color: '#00F6ED'
  }}>
    <div style={{
      width: 40,
      height: 40,
      border: '3px solid #1f2937',
      borderTopColor: '#00F6ED',
      borderRadius: '50%',
      animation: 'spin 0.8s linear infinite'
    }} />
    <style>{`@keyframes spin { to { transform: rotate(360deg) } }`}</style>
  </div>
);

// Componente temporal para páginas en desarrollo
const ComingSoon = ({ title }) => (
  <div style={{ 
    display: 'flex', 
    flexDirection: 'column', 
    alignItems: 'center', 
    justifyContent: 'center', 
    minHeight: '60vh',
    textAlign: 'center'
  }}>
    <h1 style={{ fontSize: '3rem', marginBottom: '1rem' }}>🚧</h1>
    <h2 style={{ marginBottom: '0.5rem', color: '#00F6ED' }}>{title}</h2>
    <p style={{ color: '#888' }}>Esta sección está en desarrollo</p>
  </div>
);

// ✅ S11 + S44: Redirige raíz según rol
const RootRedirect = () => {
  const { user } = useAuth();
  const dest = user?.rol === 'TECNICO' ? '/remitos'
    : user?.rol === 'VENDEDOR' ? '/chat'
    : '/dashboard';
  return <Navigate to={dest} replace />;
};

// ✅ S11: Bloquea rutas no permitidas para TECNICO
const TecnicoGuard = ({ children }) => {
  const { user } = useAuth();
  const location = useLocation();
  if (user?.rol === 'TECNICO' && !location.pathname.startsWith('/remitos')) {
    return <Navigate to="/remitos" replace />;
  }
  return children;
};

// ✅ F29: Bloquea rutas por rol — redirige a /dashboard si no tiene acceso
const RoleGuard = ({ allowed, userFlag, children }) => {
  const { user } = useAuth();
  // SUPER_ADMIN siempre pasa
  if (user?.rol === 'SUPER_ADMIN') return children;
  // Si el rol del usuario está en la lista, pasa
  if (allowed.includes(user?.rol)) return children;
  // Si tiene un permiso específico por usuario (ej: accesoBots), pasa
  if (userFlag && user?.[userFlag]) return children;
  // Si no, redirigir según rol
  const fallback = user?.rol === 'TECNICO' ? '/remitos' : user?.rol === 'VENDEDOR' ? '/chat' : '/dashboard';
  return <Navigate to={fallback} replace />;
};

// ✅ F34: Interceptor global — muestra toast en error 429 (rate limit)
const RateLimitGuard = () => {
  useEffect(() => {
    const origSend = XMLHttpRequest.prototype.send;
    XMLHttpRequest.prototype.send = function (...args) {
      this.addEventListener('load', function () {
        if (this.status === 429) {
          toast.error('Sistema procesando solicitudes. Esperá unos segundos.', {
            id: 'rate-429',
            duration: 4000,
            icon: '⏳'
          });
        }
      });
      origSend.apply(this, args);
    };
    return () => { XMLHttpRequest.prototype.send = origSend; };
  }, []);
  return null;
};

function App() {
  return (
    <BrowserRouter>
      <ToastProvider>
        <AuthProvider>
          <SocketProvider>
            {/* Toast notifications */}
            <Toaster 
              position="top-right"
              toastOptions={{
                duration: 3000,
                style: {
                  background: '#1f2937',
                  color: '#fff',
                  border: '1px solid #374151'
                },
                success: {
                  iconTheme: {
                    primary: '#10b981',
                    secondary: '#fff'
                  }
                },
                error: {
                  iconTheme: {
                    primary: '#ef4444',
                    secondary: '#fff'
                  }
                }
              }}
            />

            {/* ✅ F34: Toast global para rate limit 429 */}
            <RateLimitGuard />

            {/* ✅ HOTFIX: Alerta prominente para leads HOT/WARM — visible en TODA la app */}
            <LeadAlertOverlay />

            <Routes>
              {/* Rutas públicas (sin Layout) */}
              <Route path="/login" element={<Suspense fallback={<PageLoader />}><LoginPage /></Suspense>} />
              <Route path="/register" element={<Suspense fallback={<PageLoader />}><RegisterPage /></Suspense>} />
              
              {/* Rutas protegidas con Layout */}
              <Route
                path="/*"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <TecnicoGuard>
                      <Suspense fallback={<PageLoader />}>
                      <Routes>
                        {/* ✅ S11: Ruta raíz — TECNICO va a /remitos, resto a /dashboard */}
                        <Route path="/" element={<RootRedirect />} />
                        
                        {/* Dashboard */}
                        <Route path="/dashboard" element={<DashboardPage />} />
                        {/* ✅ F23: Dashboard Ejecutivo (solo dueño) */}
                        <Route path="/dashboard-ejecutivo" element={<DashboardEjecutivoPage />} />
                        
                        {/* Chat */}
                        <Route path="/chat" element={<ChatPage />} />
                        
                        {/* Leads */}
                        <Route path="/leads" element={<LeadsPage />} />
                        {/* ✅ S53-B2: Auditoría leads eliminados */}
                        <Route path="/leads-eliminados" element={<RoleGuard allowed={['ADMIN']} userFlag="accesoEliminarLead"><LeadsEliminadosPage /></RoleGuard>} />
                        {/* ✅ S64: Análisis de Leads (todos los roles) */}
                        <Route path="/analisis-leads" element={<AnalisisLeadsPage />} />
                        
                        {/* ============ VENTAS ============ */}
                        <Route path="/pipeline" element={<PipelinePage />} />
                        <Route path="/sales" element={<SalesPage />} />
                        <Route path="/payments" element={<PaymentsPage />} />
                        <Route path="/pagos" element={<PaymentsPage />} />
                        {/* FASE 20B: Historial de Clientes */}
                        <Route path="/historial-clientes" element={<ClienteHistorialPage />} />
                        {/* ★ N2: Timeline completo de un lead */}
                        <Route path="/historial/:leadId" element={<LeadTimelinePage />} />
                        {/* ✅ S7: Recordatorios con OK/Visto */}
                        <Route path="/recordatorios" element={<RecordatoriosPage />} />
                        {/* ✅ S9: Cuenta Corriente por lead */}
                        <Route path="/cuenta-corriente/:leadId" element={<CuentaCorrientePage />} />
                        {/* ================================ */}
                        
                        {/* ============ MARKETING ============ */}
                        {/* FASE 10: Campañas Meta Ads */}
                        <Route path="/campaigns" element={<RoleGuard allowed={['ADMIN']}><CampaignsPage /></RoleGuard>} />
                        
                        {/* FASE 11: Broadcasts Masivos */}
                        <Route path="/broadcasts" element={<RoleGuard allowed={['ADMIN']}><BroadcastsPage /></RoleGuard>} />
                        
                        {/* ✅ F24: Catálogo de Productos */}
                        <Route path="/productos" element={<ProductCatalogPage />} />

                        {/* ✅ F25B: Meta Templates */}
                        <Route path="/meta-templates" element={<RoleGuard allowed={['ADMIN']}><MetaTemplatesPage /></RoleGuard>} />
                        
                        {/* ✅ S10: Informes (reemplaza Analytics) */}
                        <Route path="/informes" element={<RoleGuard allowed={['ADMIN']}><InformesPage /></RoleGuard>} />
                        {/* ================================== */}

                        {/* ============ OPERACIONES F26 ============ */}
                        <Route path="/stock" element={<StockPage />} />
                        <Route path="/ordenes-trabajo" element={<OrdenesTrabajoPage />} />
                        <Route path="/remitos" element={<RemitosPage />} />
                        <Route path="/facturacion" element={<RoleGuard allowed={['ADMIN']}><FacturacionPage /></RoleGuard>} />
                        <Route path="/relevamientos" element={<RelevamientoPage />} />
                        {/* ======================================== */}
                        
                        {/* ============ CONFIGURACIÓN ============ */}
                        {/* FASE 13: Configuración de Bots */}
                        <Route path="/bots" element={<RoleGuard allowed={['ADMIN']} userFlag="accesoBots"><BotsPage /></RoleGuard>} />
                        {/* ★ N1: Configurar plantilla de presupuesto */}
                        <Route path="/plantilla-presupuesto" element={<RoleGuard allowed={['ADMIN']}><PlantillaPresupuestoPage /></RoleGuard>} />
                        <Route path="/service-alerts" element={<RoleGuard allowed={['ADMIN', 'VENDEDOR']}><ServiceAlertsPage /></RoleGuard>} />
                        <Route path="/config-pagos" element={<RoleGuard allowed={['ADMIN']}><ConfigPagosPage /></RoleGuard>} />
                        {/* FASE 14: Planes y Facturación */}
                        <Route path="/plans" element={<PlansPage />} />
                        {/* ====================================== */}

                        {/* ============ SUPER ADMIN — FASE 19 + 19E ============ */}
                        <Route
                          path="/admin/dashboard"
                          element={
                            <ProtectedRoute requireAdmin>
                              <AdminDashboardPage />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/admin/clients"
                          element={
                            <ProtectedRoute requireAdmin>
                              <AdminClientsPage />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/admin/bot-templates"
                          element={
                            <ProtectedRoute requireAdmin>
                              <AdminBotTemplatesPage />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/admin/settings"
                          element={
                            <ProtectedRoute requireAdmin>
                              <AdminConfigPage />
                            </ProtectedRoute>
                          }
                        />
                        {/* ===================================================== */}
                        
                        {/* Ruta 404 */}
                        <Route 
                          path="*" 
                          element={
                            <div style={{ 
                              display: 'flex', 
                              flexDirection: 'column', 
                              alignItems: 'center', 
                              justifyContent: 'center', 
                              minHeight: '60vh',
                              textAlign: 'center'
                            }}>
                              <h1 style={{ fontSize: '4rem', marginBottom: '1rem' }}>404</h1>
                              <h2 style={{ marginBottom: '0.5rem' }}>Página no encontrada</h2>
                              <p style={{ color: '#888' }}>La página que buscas no existe</p>
                            </div>
                          } 
                        />
                      </Routes>
                      </Suspense>
                      </TecnicoGuard>
                      <DemoTour />
                    </Layout>
                  </ProtectedRoute>
                }
              />
            </Routes>
          </SocketProvider>
        </AuthProvider>
      </ToastProvider>
    </BrowserRouter>
  );
}

export default App;
